import { createLogger } from "@/lib/frontend-logger";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";
import { TOKEN_KEY } from "@/lib/auth/PowtoonAuthContext";

const logger = createLogger("scenarios.geminiFilesClient");

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

function getPowtoonToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(TOKEN_KEY);
}

interface GeminiFilesResponse {
  file_uri: string;
  expires_at: string;
  state: "ACTIVE" | "PROCESSING" | "FAILED" | "STATE_UNSPECIFIED";
}

interface StartUploadResponse {
  upload_url: string;
  upload_id: string;
}

async function post<T>(action: string, payload: Record<string, unknown>): Promise<T> {
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  let res: Response;
  const headers: Record<string, string> = {
    "content-type": "application/json",
    apikey: SUPABASE_KEY,
    authorization: `Bearer ${SUPABASE_KEY}`,
    [CORRELATION_HEADER]: correlationId,
  };
  const powtoonToken = getPowtoonToken();
  if (powtoonToken) headers["x-powtoon-token"] = powtoonToken;

  try {
    res = await fetch(
      appendOp(`${SUPABASE_URL}/functions/v1/gemini-files`, `gemini-files-${action}`),
      {
        method: "POST",
        headers,
        body: JSON.stringify({ action, ...payload }),
      },
    );
  } catch (e) {
    logger.error("gemini-files network error", {
      action,
      correlationId,
      error: (e as Error).message,
    });
    throw new Error(
      `Network error reaching file preparation service (correlation ${correlationId}).`,
    );
  }
  const durationMs = Math.round(performance.now() - startedAt);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    logger.error("gemini-files call failed", {
      action,
      status: res.status,
      correlationId,
      durationMs,
      error: body?.error,
    });
    throw new Error(
      typeof body?.error === "string" && body.error.trim()
        ? `${body.error.trim()} [ref ${correlationId}]`
        : `File preparation failed (HTTP ${res.status}) [ref ${correlationId}]`,
    );
  }
  logger.info("gemini-files call ok", { action, status: res.status, correlationId, durationMs });
  return body as T;
}

export async function ensureGeminiFile(input: {
  scenario_id: string;
  object_id: string;
  mime: string;
  file_name: string;
}): Promise<GeminiFilesResponse> {
  return post<GeminiFilesResponse>("ensure", input);
}

/**
 * Stream a draft file's bytes to the edge function, which relays them to the
 * Gemini Files API. Browsers cannot PUT to Google's resumable upload URL
 * directly (CORS preflight is rejected and Content-Length is a forbidden
 * header), so the proxy is the only workable transport pre-save.
 */
export async function uploadDraftFileToGemini(
  file: File | Blob,
  fileName: string,
  mime: string,
): Promise<GeminiFilesResponse> {
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  const headers: Record<string, string> = {
    "content-type": "application/octet-stream",
    apikey: SUPABASE_KEY,
    authorization: `Bearer ${SUPABASE_KEY}`,
    [CORRELATION_HEADER]: correlationId,
    "x-gemini-action": "upload",
    "x-gemini-file-name": encodeURIComponent(fileName),
    "x-gemini-mime": mime,
  };
  const powtoonToken = getPowtoonToken();
  if (powtoonToken) headers["x-powtoon-token"] = powtoonToken;

  let res: Response;
  try {
    res = await fetch(appendOp(`${SUPABASE_URL}/functions/v1/gemini-files`, "gemini-files-upload"), {
      method: "POST",
      headers,
      body: file,
    });
  } catch (e) {
    logger.error("gemini-files upload network error", {
      correlationId,
      error: (e as Error).message,
    });
    throw new Error(
      `Network error reaching file preparation service (correlation ${correlationId}).`,
    );
  }

  const durationMs = Math.round(performance.now() - startedAt);
  const body = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (!res.ok) {
    logger.error("gemini-files upload failed", {
      status: res.status,
      correlationId,
      durationMs,
      error: (body as { error?: string })?.error,
    });
    const message = (body as { error?: string })?.error;
    throw new Error(
      typeof message === "string" && message.trim()
        ? `${message.trim()} [ref ${correlationId}]`
        : `File preparation failed (HTTP ${res.status}) [ref ${correlationId}]`,
    );
  }
  logger.info("gemini-files upload ok", { status: res.status, correlationId, durationMs });
  return body as GeminiFilesResponse;
}

